import React, { useState } from 'react';
import './MenuManager.css';
import { useKitchenData } from './Context/KitchenDataContext';
import KitchenSidebar from './KitchenSidebar';

const MenuManager = () => {
  const { menuItems, addMenuItem, updateMenuItem, deleteMenuItem } = useKitchenData();
  
  // Form states
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Main Course');
  const [ingredients, setIngredients] = useState([]);
  const [currentIngredient, setCurrentIngredient] = useState({ name: '', qty: '', unit: 'g' });
  
  // Nutrition form states
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  
  // Edit mode
  const [editingCode, setEditingCode] = useState(null);

  const addIngredient = () => {
    if (!currentIngredient.name || !currentIngredient.qty) return;
    
    setIngredients([...ingredients, { ...currentIngredient }]);
    setCurrentIngredient({ name: '', qty: '', unit: 'g' });
  };

  const removeIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setCode('');
    setName('');
    setDescription('');
    setCategory('Main Course');
    setIngredients([]);
    setCurrentIngredient({ name: '', qty: '', unit: 'g' });
    setCalories('');
    setProtein('');
    setCarbs('');
    setFat('');
    setEditingCode(null);
  };

  const handleSubmit = () => {
    if (!code || !name || ingredients.length === 0) {
      alert('Please fill in code, name, and at least one ingredient');
      return;
    }

    const menuItem = {
      code: code.toUpperCase(),
      name,
      description: description || `${name} - delicious and nutritious`,
      ingredients,
      nutrition: {
        calories: parseInt(calories) || 0,
        protein: parseFloat(protein) || 0,
        carbs: parseFloat(carbs) || 0,
        fat: parseFloat(fat) || 0
      },
      category
    };

    if (editingCode) {
      updateMenuItem(menuItem);
    } else {
      addMenuItem(menuItem);
    }
    
    resetForm();
  };

  const handleEdit = (menuItem) => {
    setCode(menuItem.code);
    setName(menuItem.name);
    setDescription(menuItem.description);
    setCategory(menuItem.category);
    setIngredients([...menuItem.ingredients]);
    setCalories(menuItem.nutrition.calories.toString());
    setProtein(menuItem.nutrition.protein.toString());
    setCarbs(menuItem.nutrition.carbs.toString());
    setFat(menuItem.nutrition.fat.toString());
    setEditingCode(menuItem.code);
  };

  const handleDelete = (code) => {
    if (window.confirm(`Are you sure you want to delete ${code}?`)) {
      deleteMenuItem(code);
    }
  };

  return (
    <div className="mm-kitchen-layout">
      <KitchenSidebar />
      <div className="mm-kitchen-root col-10">
        <div className="mm-kitchen-header" style={{ marginBottom: 8 }}>
          <div>
            <h1 className="mm-kh-title">Menu Manager</h1>
            <div className="mm-kh-date">
              {editingCode ? `Editing ${editingCode}` : 'Add or edit food menu items'}
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        <div className="mm-kh-panel" style={{ marginBottom: 16 }}>
          <div className="mm-kh-panel-title">
            {editingCode ? `Edit Menu Item: ${editingCode}` : 'Add New Menu Item'}
          </div>
          
          {/* Basic Info */}
          <div style={{ padding: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 200px', gap: 12, marginBottom: 12 }}>
              <input 
                placeholder="Code (e.g., CHW)" 
                className="mm-kh-search" 
                value={code} 
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={editingCode}
                maxLength={3}
              />
              <input 
                placeholder="Menu Item Name" 
                className="mm-kh-search" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
              />
              <select 
                className="mm-kh-search" 
                value={category} 
                onChange={(e) => setCategory(e.target.value)}
              >
                <option>Main Course</option>
                <option>Beverages</option>
                <option>Salads</option>
                <option>Soups</option>
                <option>Desserts</option>
                <option>Snacks</option>
              </select>
            </div>
            
            <textarea 
              placeholder="Description (optional)" 
              className="mm-kh-search" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: '100%', minHeight: '60px', marginBottom: 12 }}
            />

            {/* Nutrition Info */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
              <input 
                placeholder="Calories" 
                className="mm-kh-search" 
                type="number" 
                value={calories} 
                onChange={(e) => setCalories(e.target.value)} 
              />
              <input 
                placeholder="Protein (g)" 
                className="mm-kh-search" 
                type="number" 
                step="0.1"
                value={protein} 
                onChange={(e) => setProtein(e.target.value)} 
              />
              <input 
                placeholder="Carbs (g)" 
                className="mm-kh-search" 
                type="number" 
                step="0.1"
                value={carbs} 
                onChange={(e) => setCarbs(e.target.value)} 
              />
              <input 
                placeholder="Fat (g)" 
                className="mm-kh-search" 
                type="number" 
                step="0.1"
                value={fat} 
                onChange={(e) => setFat(e.target.value)} 
              />
            </div>

            {/* Add Ingredient */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 100px auto', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <input 
                placeholder="Ingredient name" 
                className="mm-kh-search" 
                value={currentIngredient.name} 
                onChange={(e) => setCurrentIngredient({...currentIngredient, name: e.target.value})} 
              />
              <input 
                placeholder="Quantity" 
                className="mm-kh-search" 
                value={currentIngredient.qty} 
                onChange={(e) => setCurrentIngredient({...currentIngredient, qty: e.target.value})} 
              />
              <select 
                className="mm-kh-search" 
                value={currentIngredient.unit} 
                onChange={(e) => setCurrentIngredient({...currentIngredient, unit: e.target.value})}
              >
                <option value="g">g</option>
                <option value="ml">ml</option>
                <option value="pieces">pieces</option>
                <option value="tbsp">tbsp</option>
                <option value="tsp">tsp</option>
                <option value="cups">cups</option>
              </select>
              <button className="mm-btn-nav" onClick={addIngredient}>Add</button>
            </div>

            {/* Ingredients List */}
            {ingredients.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <strong>Ingredients:</strong>
                <div className="mm-mp-grid" style={{ marginTop: 8 }}>
                  {ingredients.map((ing, idx) => (
                    <div key={idx} className="mm-mp-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{ing.name}</span>
                      <span className="mm-mp-qty">{ing.qty} {ing.unit}</span>
                      <button 
                        onClick={() => removeIngredient(idx)}
                        style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="mm-btn-nav" onClick={handleSubmit}>
                {editingCode ? 'Update Menu Item' : 'Add Menu Item'}
              </button>
              {editingCode && (
                <button className="mm-btn-nav" onClick={resetForm} style={{ backgroundColor: '#6c757d' }}>
                  Cancel Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items Display */}
        <div className="mm-kh-panel">
          <div className="mm-kh-panel-title">Current Menu Items ({Object.keys(menuItems).length})</div>
          <div className="mm-mp-cards">
            {Object.values(menuItems).map(item => (
              <div key={item.code} className="mm-kh-panel">
                <div className="mm-kh-panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{item.code} - {item.name}</span>
                  <div>
                    <button 
                      onClick={() => handleEdit(item)}
                      style={{ marginRight: 8, padding: '4px 8px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(item.code)}
                      style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div style={{ padding: '8px 0', fontSize: '14px', color: '#666' }}>
                  {item.description}
                </div>
                
                <div style={{ padding: '4px 0', fontSize: '12px', color: '#888' }}>
                  <strong>Category:</strong> {item.category} | 
                  <strong> Nutrition:</strong> {item.nutrition.calories} cal, {item.nutrition.protein}g protein, {item.nutrition.carbs}g carbs, {item.nutrition.fat}g fat
                </div>
                
                <div className="mm-mp-grid">
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Ingredients:</div>
                  {item.ingredients.map((ing, idx) => (
                    <div key={idx} className="mm-mp-row">
                      <span>{ing.name}</span>
                      <span className="mm-mp-qty">{ing.qty} {ing.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuManager;